import type { ReactNode } from "react";

import type { Product } from "../data/products";

/**
 * App Store / Play Store buttons.
 *
 * Drawn rather than embedded as Apple's and Google's official badge PNGs:
 * the official artwork is fixed-colour raster that sits badly on a dark
 * ground, ships two more image requests, and cannot be made to match the
 * site's button geometry. Both companies' brand guidelines permit a plain
 * text link to a listing, and the marks here are the plain corporate
 * glyphs at the size those guidelines specify.
 *
 * Renders nothing when a product has no store presence, so a web-only
 * product never shows an empty row.
 */
export function StoreBadges({
  product,
  className = "",
}: {
  product: Product;
  className?: string;
}) {
  const { stores } = product;
  if (!stores?.ios && !stores?.android) return null;

  return (
    <div className={className}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/35">
        Get the worker app
      </div>
      <div className="mt-3 flex flex-wrap gap-2.5">
        {stores.ios ? (
          <Badge
            href={stores.ios}
            label={`${product.name} on the App Store`}
            top="Download on the"
            bottom="App Store"
            icon={<AppleMark />}
          />
        ) : null}
        {stores.android ? (
          <Badge
            href={stores.android}
            label={`${product.name} on Google Play`}
            top="Get it on"
            bottom="Google Play"
            icon={<PlayMark />}
          />
        ) : null}
      </div>
    </div>
  );
}

function Badge({
  href,
  label,
  top,
  bottom,
  icon,
}: {
  href: string;
  label: string;
  top: string;
  bottom: string;
  icon: ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={label}
      className="group inline-flex min-h-[48px] items-center gap-3 rounded-xl border border-white/15 bg-white/[0.05] px-4 py-2.5 transition-colors hover:border-white/30 hover:bg-white/[0.09]"
    >
      <span className="shrink-0 text-white/85 transition-colors group-hover:text-white">
        {icon}
      </span>
      <span className="leading-tight">
        <span className="block text-[10px] font-medium uppercase tracking-wider text-white/50">
          {top}
        </span>
        <span className="block text-sm font-semibold text-white">{bottom}</span>
      </span>
    </a>
  );
}

function AppleMark() {
  return (
    <svg viewBox="0 0 24 24" className="size-6" fill="currentColor" aria-hidden>
      <path d="M17.05 12.94c-.02-2.3 1.88-3.4 1.96-3.46-1.07-1.56-2.73-1.78-3.32-1.8-1.41-.14-2.76.83-3.48.83-.72 0-1.83-.81-3.01-.79-1.55.02-2.98.9-3.77 2.29-1.61 2.79-.41 6.92 1.15 9.18.77 1.11 1.68 2.35 2.87 2.3 1.15-.05 1.59-.74 2.98-.74 1.39 0 1.78.74 3 .72 1.24-.02 2.02-1.12 2.78-2.23.88-1.28 1.24-2.52 1.26-2.58-.03-.01-2.41-.93-2.42-3.72ZM14.9 5.9c.63-.77 1.06-1.83.94-2.9-.91.04-2.02.61-2.67 1.37-.58.68-1.09 1.77-.95 2.81 1.02.08 2.05-.52 2.68-1.28Z" />
    </svg>
  );
}

function PlayMark() {
  return (
    <svg viewBox="0 0 24 24" className="size-6" fill="currentColor" aria-hidden>
      <path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z" />
    </svg>
  );
}
